import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const TestScreenQuestionCard = ({ data, completedQuestions, refetch, index }) => {
  const navigate = useNavigate();

  const getDifficultyStyle = (difficulty) => {
    switch (difficulty) {
      case "EASY":
        return { bg: "#E1F5EE", color: "#0F6E56", dot: "#1D9E75" };
      case "MEDIUM":
        return { bg: "#FEF3C7", color: "#92400E", dot: "#D97706" };
      case "HARD":
        return { bg: "#FEE2E2", color: "#991B1B", dot: "#DC2626" };
      default:
        return { bg: "#F3F4F6", color: "#4B5563", dot: "#9CA3AF" };
    }
  };

  useEffect(() => {
    refetch();
  }, [completedQuestions]);

  const difficulty =
    data?.difficulty?.charAt(0).toUpperCase() +
    data?.difficulty?.slice(1).toLowerCase();

  const totalScore = data.test_cases.length > 0 ? data.test_cases[0].weightage * 25 : 0;

  const question = completedQuestions?.find((ques) => ques.question_id === data.id);
  const isCompleted = question?.score === totalScore;

  const diffStyle = getDifficultyStyle(data?.difficulty);

  return (
    <div
      onClick={() => navigate(`${data.id}`)}
      className="group relative cursor-pointer rounded-xl bg-white p-5 transition-all duration-200 hover:-translate-y-0.5"
      style={{
        border: '1px solid #e5e7eb',
        borderLeft: `3px solid ${isCompleted ? '#10b981' : '#6366f1'}`,
        background: '#ffffff',
        marginTop: '14px',
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
        fontFamily: "'Poppins', sans-serif",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#f8fafc';
        e.currentTarget.style.boxShadow = "0 10px 24px rgba(15, 23, 42, 0.08)";
        e.currentTarget.style.borderColor = "#d1d5db";
        e.currentTarget.style.borderLeftColor = isCompleted ? '#10b981' : '#6366f1';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = '#ffffff';
        e.currentTarget.style.boxShadow = "0 1px 3px rgba(15, 23, 42, 0.04)";
        e.currentTarget.style.borderColor = "#e5e7eb";
        e.currentTarget.style.borderLeftColor = isCompleted ? '#10b981' : '#6366f1';
      }}
    >
      <div className="flex items-start justify-between gap-5">
        {/* Q-number badge */}
        {index !== undefined && (
          <div
            className="flex-shrink-0 flex items-center justify-center rounded-lg text-[13px] font-bold transition-colors duration-200"
            style={{
              width: 40,
              height: 40,
              background: isCompleted ? '#ECFDF5' : '#F3F4F6',
              color: isCompleted ? '#047857' : '#374151',
              border: `1px solid ${isCompleted ? '#A7F3D0' : '#E5E7EB'}`,
            }}
          >
            {String(index).padStart(2, '0')}
          </div>
        )}
        <div className="flex-1 min-w-0">
          {/* Title + difficulty inline */}
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h3 className="text-base font-semibold text-gray-900 sm:text-lg">
              {data.title}
            </h3>
            {data?.difficulty && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
                style={{ background: diffStyle.bg, color: diffStyle.color }}
              >
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full"
                  style={{ background: diffStyle.dot }}
                />
                {difficulty}
              </span>
            )}
            {isCompleted && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Completed
              </span>
            )}
          </div>

          {/* Meta row */}
          <div className="flex items-center gap-3 text-[13px] text-gray-500 mb-3">
            <span className="inline-flex items-center gap-1.5">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              <span className="font-medium text-gray-700">{data.marks}</span>
              <span>marks</span>
            </span>
            {data?.test_cases?.length > 0 && (
              <>
                <span className="w-1 h-1 bg-gray-300 rounded-full" />
                <span className="inline-flex items-center gap-1.5">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 11H5a2 2 0 0 0-2 2v7h6v-9z" />
                    <path d="M15 7h-4v13h6V9a2 2 0 0 0-2-2z" />
                    <path d="M21 3h-4v17h4V3z" />
                  </svg>
                  <span className="font-medium text-gray-700">{data.test_cases.length}</span>
                  <span>test{data.test_cases.length === 1 ? "" : "s"}</span>
                </span>
              </>
            )}
          </div>

          {/* Tags */}
          {data?.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {data.tags.map((tag, i) => {
                return (
                  <span
                    key={i}
                    className="inline-block rounded-md px-2.5 py-0.5 text-[11px] font-semibold"
                    style={{ background: '#6b7280', color: '#ffffff' }}
                  >
                    {tag}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Chevron affordance */}
        <div
          className="flex items-center justify-center flex-shrink-0 text-gray-400 transition-all duration-200 rounded-full w-9 h-9 bg-gray-50 group-hover:bg-gray-900 group-hover:text-white"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default TestScreenQuestionCard;